import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
  ActivityInterface,
  ActivityStateTypes,
  CreateActivityInterface,
  UpdateActivityInterface,
  useActivitiesContext,
} from '../activities.context';

interface HandleSaveActivityInterface {
  state?: ActivityStateTypes;
  id?: number;
}

export interface StepFiles {
  file: File;
  stepIndex: number;
}

interface ActivityWizardContextInterface {
  newActivity: CreateActivityInterface | UpdateActivityInterface;
  setNewActivity: (activity: CreateActivityInterface | UpdateActivityInterface) => void;
  existingActivity: ActivityInterface | undefined;
  setExistingActivity: (activity: ActivityInterface) => void;
  handleSaveActivity: ({ state, id }: HandleSaveActivityInterface) => void;
  loading: boolean;
}

export const defaultActivity: CreateActivityInterface = {
  subject: '',
  description: '',
  assignedUserIds: [],
  ccRecipientIds: [],
  steps: [],
  tagIds: [],
  state: 'Draft',
};

const defaultContext: ActivityWizardContextInterface = {
  handleSaveActivity: () => {},
  newActivity: defaultActivity,
  setNewActivity: () => {},
  existingActivity: undefined,
  setExistingActivity: () => {},
  loading: false,
};

const ActivityWizardContext = createContext<ActivityWizardContextInterface>(defaultContext);

export const ActivityWizardContextProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [newActivity, setNewActivity] = useState<CreateActivityInterface | UpdateActivityInterface>(
    defaultContext.newActivity
  );
  const [existingActivity, setExistingActivity] = useState<ActivityInterface>();
  const { addActivity, editActivity } = useActivitiesContext();

  const handleSaveActivity = async ({ state, id }: HandleSaveActivityInterface) => {
    setLoading(true);

    if (id) {
      await editActivity({ ...newActivity, id }, state);
    } else {
      await addActivity(newActivity, state);
    }
  };

  return (
    <ActivityWizardContext.Provider
      value={{
        newActivity,
        handleSaveActivity,
        setNewActivity,
        setExistingActivity,
        existingActivity,
        loading,
      }}
    >
      {children}
    </ActivityWizardContext.Provider>
  );
};

export const useActivityWizardContext = () => useContext(ActivityWizardContext);
